import RichTextField from './RichTextField';
import TimeField from './TimeField';
/* eslint react/prop-types: 0 */
import React from 'react';
import InputAdornment from '@mui/material/InputAdornment';
import {
  percentagePrecision,
  percentageValueError
} from '../../../../common/percentageFormat';
import TextField from '@mui/material/TextField';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';

import './styles.scss';

const verticalMenuProps = {
  MenuListProps: {
    sx: {
      '&&': {
        alignItems: 'stretch',
        display: 'flex',
        flexDirection: 'column'
      }
    }
  }
};

const selectSx = {
  '&& .MuiSelect-select': {
    textAlign: 'left'
  }
};

const menuItemSx = {
  '&&': {
    display: 'flex',
    flex: '0 0 auto',
    justifyContent: 'flex-start',
    width: '100%'
  }
};

//Helpers for DateTime, DateOnly and URL
const pad2 = (n) => String(n).padStart(2, 0);

const toDateOnlyInput = (v) => {
  if (!v) return '';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    const d = new Date(v);
    if (!isNaN(d.getTime())) {
      return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    }
    return '';
  }
  if (v instanceof Date && !isNaN(v.getTime())) {
    return `${v.getFullYear()}-${pad2(v.getMonth() + 1)}-${pad2(v.getDate())}`;
  }
  return;
};

const toDatetimeLocalInput = (v) => {
  if (!v) return '';
  const d = typeof v === 'string' ? new Date(v) : v instanceof Date ? v : null;
  if (!d || isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const parseLinkValue = (raw) => {
  if (!raw) return { url: '', text: '' };
  if (typeof raw === 'string') {
    const s = raw.trim();
    if (!s) return { url: '', text: '' };
    if (s.startsWith('{') && s.endsWith('}')) {
      try {
        const obj = JSON.parse(s);
        return { url: String(obj.url || ''), text: String(obj.text || '') };
      } catch {}
    }
    return { url: s, text: '' };
  }
  if (typeof raw === 'object') {
    return { url: String(raw.url || ''), text: String(raw.text || '') };
  }
  return { url: '', text: '' };
};

const toUrlJsonString = ({ url, text }) =>
  JSON.stringify({ url: (url || '').trim(), text: text || '' });
/**
 * @param {{
 * field: import("../../../hooks/use-form-data").Field;
 * fieldType: "text" | "number" | "dropdown" | "multiselect" | "checkbox" | "dateonly" | "datetime" | "url";
 * values: Record<string, string | number | boolean>;
 * handleChange: VoidFunction;
 * handleBlur: VoidFunction;
 * }} props
 * @returns
 */
const RenderField = ({
  field,
  fieldType,
  values,
  handleBlur,
  handleChange
}) => {
  /**
   * @param {import("../../../hooks/use-form-data").Field} item
   * @returns {JSX}
   */
  const renderTextField = (item) => (
    <TextField
      size="small"
      fullWidth
      id={item.field_id}
      name={item.field_id}
      value={values[item.field_id]}
      onChange={handleChange}
      onBlur={handleBlur}
      // TODO: add error handling in further updates
      // error={formik.touched.email && Boolean(formik.errors.email)}
      // helperText={formik.touched.email && formik.errors.email}
    />
  );

  /**
   * @param {import("../../../hooks/use-form-data").Field} item
   * @returns {JSX}
   */
  const renderNumberField = (item) => {
    const config = item.field.config || {};
    const percentage = config.format === 'percentage';
    const error = percentage
      ? percentageValueError(values[item.field_id], config)
      : '';
    return (
      <TextField
        size="small"
        fullWidth
        type="number"
        id={item.field_id}
        name={item.field_id}
        value={values[item.field_id] ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(error)}
        helperText={
          error || (percentage ? 'Enter 0–100; 60 means 60%.' : undefined)
        }
        inputProps={
          percentage
            ? {
                min: 0,
                max: 100,
                step: 10 ** -percentagePrecision(config),
                'aria-label': item.field.label
              }
            : undefined
        }
        InputProps={
          percentage
            ? {
                endAdornment: <InputAdornment position="end">%</InputAdornment>
              }
            : undefined
        }
      />
    );
  };

  /**
   * @param {import("../../../hooks/use-form-data").Field} item
   * @returns {JSX}
   */
  const renderSelectField = (item) => (
    <Select
      size="small"
      fullWidth
      id={item.field_id}
      name={item.field_id}
      value={values[item.field_id]}
      onChange={handleChange}
      displayEmpty
      MenuProps={verticalMenuProps}
      sx={selectSx}
    >
      <MenuItem value="" sx={menuItemSx}>
        <em>Select an option</em>
      </MenuItem>
      {item.field.config.options.map(({ key, value }) => (
        <MenuItem key={key} value={key} sx={menuItemSx}>
          {value}
        </MenuItem>
      ))}
    </Select>
  );

  /**
   * @param {import("../../../hooks/use-form-data").Field} item
   * @returns {JSX}
   */
  const renderCheckbox = (item) => (
    <FormControl
      component="fieldset"
      // TODO: add error handling in further updates
      //   error={error}
      //   className={classes.formControl}
    >
      <RadioGroup
        aria-label={item.field_id}
        id={item.field_id}
        name={item.field_id}
        value={String(values[item.field_id]) === 'true'}
        className="checkbox-wrapper"
        onChange={handleChange}
      >
        <FormControlLabel
          value={false}
          control={<Radio size="small" />}
          label="No"
        />
        <FormControlLabel
          value={true}
          control={<Radio size="small" />}
          label="Yes"
        />
      </RadioGroup>
    </FormControl>
  );

  /**
   * @param {import("../../../hooks/use-form-data").Field} item
   * @returns {JSX}
   */
  const renderDateOnly = (item) => (
    <TextField
      size="small"
      fullWidth
      type="date"
      InputLabelProps={{ shrink: true }}
      id={item.field_id}
      name={item.field_id}
      value={toDateOnlyInput(values[item.field_id])}
      onChange={handleChange}
    />
  );

  /**
   * @param {import("../../../hooks/use-form-data").Field} item
   * @returns {JSX}
   */
  const renderDateTime = (item) => (
    <TextField
      size="small"
      fullWidth
      type="datetime-local"
      InputLabelProps={{ shrink: true }}
      id={item.field_id}
      name={item.field_id}
      value={toDatetimeLocalInput(values[item.field_id])}
      onChange={(e) => {
        const { name, value } = e.target;
        const iso = value ? new Date(value).toISOString() : '';
        handleChange({ target: { name, value: iso } });
      }}
    />
  );

  const renderUrl = (item) => {
    const name = item.field_id;
    const { url, text } = parseLinkValue(values[name]);

    const emit = (next) =>
      handleChange({ target: { name, value: toUrlJsonString(next) } });

    return (
      <div className="url-field">
        <TextField
          size="small"
          fullWidth
          id={`${name}__url`}
          name={`${name}__url`}
          label="Link"
          value={url}
          onChange={(e) => emit({ url: e.target.value, text })}
          placeholder="https://example.com"
          style={{ marginBottom: 8 }}
        />
        <TextField
          size="small"
          fullWidth
          id={`${name}__text`}
          name={`${name}__text`}
          label="Display text (optional)"
          value={text}
          onChange={(e) => emit({ url, text: e.target.value })}
          inputProps={{ maxLength: 100 }}
          placeholder="e.g, Example"
        />
      </div>
    );
  };

  /**
   * @param {import("../../../hooks/use-form-data").Field} item
   * @returns {JSX}
   */
  const renderMultiselect = (item) => (
    <Select
      size="small"
      fullWidth
      multiple
      id={item.field_id}
      name={item.field_id}
      value={values[item.field_id]}
      onChange={handleChange}
      renderValue={(selected) => selected.join(', ')}
      MenuProps={verticalMenuProps}
      sx={selectSx}
    >
      <MenuItem value="" sx={menuItemSx}>
        <em>Select an option</em>
      </MenuItem>
      {item.field.config.options.map(({ key, value }) => (
        <MenuItem key={key} value={key} sx={menuItemSx}>
          {value}
        </MenuItem>
      ))}
    </Select>
  );

  /**
   * @type {Record<typeof fieldType, (field) => JSX>}
   */
  const renderFieldStrategy = {
    richtext: (item) => (
      <RichTextField
        name={item.field_id}
        label={item.field.label}
        value={values[item.field_id]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    ),
    text: renderTextField,
    number: renderNumberField,
    dropdown: renderSelectField,
    multiselect: renderMultiselect,
    checkbox: renderCheckbox,
    time: (item) => (
      <TimeField
        key={item.field?.config?.clock_format || '24h'}
        item={item}
        value={values[item.field_id]}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    ),
    dateonly: renderDateOnly,
    datetime: renderDateTime,
    url: renderUrl
  };

  const renderFactory = renderFieldStrategy[fieldType];

  if (!renderFactory) {
    return <div>Invalid field type</div>;
  }

  return renderFactory(field);
};

export default RenderField;
